import axios from 'axios';

const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3000',
  withCredentials: true,
});

// Automatically attach token to every request
API.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Fund Managers
export const getFundManagers = () => API.get('/fund-managers');
export const createFundManager = (data) => API.post('/fund-managers', data);

// Funds
export const getFunds = () => API.get('/funds');
export const getFund  = (id) => API.get(`/funds/${id}`);
export const createFund = (data) => API.post('/funds', data);
export const updateFund = (id, data) => API.patch(`/funds/${id}`, data);
export const deleteFund = (id) => API.delete(`/funds/${id}`);

// Investors
export const getInvestors = () => API.get('/investors');
export const getInvestor = (id) => API.get(`/investors/${id}`);
export const getInvestorStatus = (id) => API.get(`/investors/${id}/status`);
export const getSuitabilityTranscript = (investorId) =>
  API.get(`/investors/${investorId}/suitability-transcript`);
export const getAmlRating    = (investorId) => API.get(`/aml/${investorId}`);
export const runAmlAnalysis  = (investorId) => API.post(`/aml/analyse/${investorId}`);
export const getNotifications = () => API.get('/notifications');
export const getInvestorReportUrl = (investorId) => {
  const token = localStorage.getItem('token');
  const base  = process.env.REACT_APP_API_URL || 'http://localhost:3000';
  return `${base}/reports/investor/${investorId}/pdf?token=${token}`;
};
export const overrideSuitability = (assessmentId, data) => API.post(`/suitability/override/${assessmentId}`, data);
export const updateInvestorStatus = (id, data) => API.patch(`/investors/${id}/status`, data);
export const sendInvestorInvite = (id) => API.post(`/investors/${id}/invite`);
export const createInvestor = (data) => API.post('/investors', data);

// Audit Log
export const getAuditLog = (params) => API.get('/audit-log', { params });

// DORA
export const getIncidents = () => API.get('/dora/incidents');
export const getIncident = (id) => API.get(`/dora/incidents/${id}`);
export const updateIncident = (id, data) => API.patch(`/dora/incidents/${id}`, data);

// Documents
export const uploadDocument = (formData) => API.post('/documents/upload', formData, {
  headers: { 'Content-Type': 'multipart/form-data' },
});
export const getDocumentsByFund = (fundId) => API.get(`/documents/fund/${fundId}`);
export const getDocument = (documentId) => API.get(`/documents/${documentId}`);
export const approveDocument = (documentId) => API.patch(`/documents/${documentId}/approve`);

// Suitability
export const getSuitabilityAssessments = () => API.get('/suitability/assessments');
export const getSuitabilitySession = (token) => API.get(`/suitability/session/${token}`);
export const overrideAssessment = (assessmentId, data) => API.post(`/suitability/override/${assessmentId}`, data);
export const getSuitabilityReportUrl = (assessmentId) => {
  const token = localStorage.getItem('token');
  const base = process.env.REACT_APP_API_URL || 'http://localhost:3000';
  return `${base}/reports/suitability/${assessmentId}/pdf?token=${token}`;
};

// Auth profile
export const changePassword = (data) => API.patch('/auth/change-password', data);
export const changeEmail = (data) => API.patch('/auth/change-email', data);

// Preferences
export const getPreferences = () => API.get('/users/me/preferences');
export const updatePreferences = (data) => API.patch('/users/me/preferences', data);

// Maintenance
export const getMaintenanceStatus = () => API.get('/maintenance/status');
export const startMaintenance = (reason) => API.post('/maintenance/start', { reason });
export const endMaintenance = () => API.post('/maintenance/end');

// Auth
export const login = (data) => API.post('/auth/login', data);
export const forgotPassword = (data) => API.post('/auth/forgot-password', data);
export const resetPassword = (data) => API.post('/auth/reset-password', data);

// Auto-refresh access token on 401, then retry original request
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

API.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;

    // Don't retry refresh endpoint itself or pre_2fa endpoints
    const isAuthEndpoint = originalRequest.url?.includes('/auth/');
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return API(originalRequest);
        }).catch(err => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const res = await API.post('/auth/token/refresh', {}, { withCredentials: true });
        const newToken = res.data.token;
        localStorage.setItem('token', newToken);
        API.defaults.headers.common.Authorization = `Bearer ${newToken}`;
        processQueue(null, newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return API(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    if (error.response?.status === 401 && isAuthEndpoint) {
      // Let auth errors pass through to the component
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);